import PageHeader from "@/components/common/PageHeader";
import { backgroundDbWorkRefetchIntervalMs } from "@/lib/background-db-work-pause";
import { monitoringService } from "@/services/monitoring.service";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Clock,
  Gauge,
  RefreshCw,
  Server,
  Shield,
  XCircle,
  Zap,
} from "lucide-react";
import { useState } from "react";

interface SlaTarget {
  metric: string;
  target: string;
  current: number;
  passed: boolean;
}

interface TenantRanking {
  tenantId: string;
  tenantName: string;
  totalRequests: number;
  totalErrors: number;
  avgLatency: number;
  p95Latency: number;
  p99Latency: number;
}

interface SyncGridRow {
  tenantId: string;
  tenantName: string;
  totalSyncs24h: number;
  successRate: number;
  failedCount: number;
  pendingCount: number;
  unresolvedConflicts: number;
  risk: "healthy" | "warning" | "critical";
}

const HOURS_OPTIONS = [6, 12, 24, 48, 72] as const;

export default function PerformanceDashboardPage() {
  const [hours, setHours] = useState(24);
  const [activeTab, setActiveTab] = useState<"sla" | "sync" | "ranking">("sla");

  const { data: sla, isLoading: slaLoading } = useQuery({
    queryKey: ["sla", "overview", hours],
    queryFn: () => monitoringService.getSlaOverview(hours),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(30_000),
  });

  const { data: syncOverview } = useQuery({
    queryKey: ["sync", "overview"],
    queryFn: () => monitoringService.getSyncOverview(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(30_000),
  });

  const { data: ranking } = useQuery({
    queryKey: ["sla", "ranking", hours],
    queryFn: () => monitoringService.getLatencyRanking(hours, 20),
  });

  const { data: syncGrid } = useQuery({
    queryKey: ["sync", "grid"],
    queryFn: () => monitoringService.getSyncGrid(1, 50),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(30_000),
  });

  const tabs = [
    { key: "sla" as const, label: "SLA & Latency", icon: Shield },
    { key: "sync" as const, label: "Sync Health", icon: RefreshCw },
    { key: "ranking" as const, label: "Tenant Ranking", icon: Gauge },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Performance Dashboard"
        subtitle="SLA compliance, sync health, and per-tenant performance"
      />

      {/* Time range selector */}
      <div className="flex items-center gap-3">
        <Clock className="h-4 w-4 text-gray-400" />
        <span className="text-sm text-gray-500">Time range:</span>
        {HOURS_OPTIONS.map((h) => (
          <button
            key={h}
            onClick={() => setHours(h)}
            className={`rounded-md px-3 py-1 text-sm font-medium ${
              hours === h
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {h}h
          </button>
        ))}
      </div>

      {/* SLA Overview Cards */}
      {sla && (
        <div className="grid gap-4 sm:grid-cols-5">
          {(sla as SlaTarget[]).map((target) => (
            <SlaCard key={target.metric} target={target} />
          ))}
        </div>
      )}

      {/* Sync Overview Cards */}
      {syncOverview && (
        <div className="grid gap-4 sm:grid-cols-4">
          <MetricCard
            label="Syncs (24h)"
            value={syncOverview.totalSyncs24h}
            icon={Activity}
            color="text-blue-600"
          />
          <MetricCard
            label="Success Rate"
            value={`${syncOverview.successRate.toFixed(1)}%`}
            icon={syncOverview.successRate >= 95 ? CheckCircle2 : AlertTriangle}
            color={
              syncOverview.successRate >= 95
                ? "text-green-600"
                : "text-amber-600"
            }
          />
          <MetricCard
            label="Pending"
            value={syncOverview.pendingCount}
            icon={Clock}
            color="text-gray-600"
          />
          <MetricCard
            label="Unresolved Conflicts"
            value={syncOverview.unresolvedConflicts}
            icon={AlertTriangle}
            color={
              syncOverview.unresolvedConflicts > 0
                ? "text-red-600"
                : "text-green-600"
            }
          />
        </div>
      )}

      {/* Tab navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(({ key, label, icon: TabIcon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium ${
                activeTab === key
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <TabIcon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "sla" && (
        <SlaDetailSection sla={sla} isLoading={slaLoading} />
      )}
      {activeTab === "sync" && <SyncGridSection data={syncGrid} />}
      {activeTab === "ranking" && <RankingSection data={ranking} />}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   SLA Card
   ──────────────────────────────────────────────────────────── */

function SlaCard({ target }: { target: SlaTarget }) {
  const icon = target.passed ? CheckCircle2 : XCircle;
  const Icon = icon;
  return (
    <div
      className={`rounded-lg border p-4 ${
        target.passed
          ? "border-green-200 bg-green-50"
          : "border-red-200 bg-red-50"
      }`}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase text-gray-500">
          {target.metric}
        </p>
        <Icon
          className={`h-4 w-4 ${
            target.passed ? "text-green-500" : "text-red-500"
          }`}
        />
      </div>
      <p className="mt-2 text-2xl font-bold text-gray-900">
        {target.metric.includes("Latency")
          ? `${target.current}ms`
          : target.metric.includes("Rate")
            ? `${target.current}%`
            : `${target.current}%`}
      </p>
      <p className="text-xs text-gray-500">Target: {target.target}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Metric Card
   ──────────────────────────────────────────────────────────── */

function MetricCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: React.ReactNode;
  icon: typeof Activity;
  color: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${color}`} />
        <p className="text-xs font-medium uppercase text-gray-500">{label}</p>
      </div>
      <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   SLA Detail Section
   ──────────────────────────────────────────────────────────── */

function SlaDetailSection({
  sla,
  isLoading,
}: {
  sla: SlaTarget[] | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="py-12 text-center text-gray-400">Loading SLA data...</div>
    );
  }
  if (!sla || sla.length === 0) {
    return (
      <div className="py-12 text-center text-gray-400">
        No metrics data available yet. Metrics are collected automatically as
        API traffic flows.
      </div>
    );
  }

  const allPassed = sla.every((t) => t.passed);

  return (
    <div className="space-y-4">
      <div
        className={`flex items-center gap-2 rounded-lg p-4 ${
          allPassed
            ? "bg-green-50 text-green-700"
            : "bg-amber-50 text-amber-700"
        }`}
      >
        {allPassed ? (
          <>
            <Shield className="h-5 w-5" />
            <span className="font-medium">All SLA targets are being met</span>
          </>
        ) : (
          <>
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">
              {sla.filter((t) => !t.passed).length} SLA target(s) not met
            </span>
          </>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                Metric
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                Target
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                Current
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sla.map((target) => (
              <tr key={target.metric}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  <span className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-gray-400" />
                    {target.metric}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {target.target}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                  {target.metric.includes("Latency")
                    ? `${target.current}ms`
                    : `${target.current}%`}
                </td>
                <td className="px-4 py-3">
                  {target.passed ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                      <CheckCircle2 className="h-3 w-3" /> Passing
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                      <XCircle className="h-3 w-3" /> Failing
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Sync Grid Section
   ──────────────────────────────────────────────────────────── */

function SyncGridSection({
  data,
}: {
  data: { data: SyncGridRow[]; total: number } | undefined;
}) {
  if (!data) {
    return (
      <div className="py-12 text-center text-gray-400">
        Loading sync data...
      </div>
    );
  }

  const riskColors: Record<string, string> = {
    healthy: "bg-green-100 text-green-700",
    warning: "bg-amber-100 text-amber-700",
    critical: "bg-red-100 text-red-700",
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Tenant
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Syncs (24h)
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Success Rate
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Failed
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Pending
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Conflicts
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Risk
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.data.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                No sync activity in the last 24 hours
              </td>
            </tr>
          ) : (
            data.data.map((row) => (
              <tr key={row.tenantId} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {row.tenantName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {row.totalSyncs24h}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={
                      row.successRate >= 95
                        ? "text-green-600"
                        : row.successRate >= 80
                          ? "text-amber-600"
                          : "text-red-600"
                    }
                  >
                    {row.successRate}%
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-red-600">
                  {row.failedCount}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {row.pendingCount}
                </td>
                <td className="px-4 py-3 text-sm text-amber-600">
                  {row.unresolvedConflicts}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      riskColors[row.risk] ?? riskColors.healthy
                    }`}
                  >
                    {row.risk}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Ranking Section
   ──────────────────────────────────────────────────────────── */

function RankingSection({ data }: { data: TenantRanking[] | undefined }) {
  if (!data) {
    return (
      <div className="py-12 text-center text-gray-400">
        Loading ranking data...
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Server className="h-4 w-4" />
          Tenant Latency Ranking (sorted by P95)
        </h3>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              #
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Tenant
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Requests
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Errors
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              Avg Latency
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              P95 Latency
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
              P99 Latency
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                No metric data available
              </td>
            </tr>
          ) : (
            data.map((tenant, idx) => {
              const errorRate =
                tenant.totalRequests > 0
                  ? (tenant.totalErrors / tenant.totalRequests) * 100
                  : 0;
              return (
                <tr key={tenant.tenantId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-400">
                    {idx + 1}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {tenant.tenantName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {tenant.totalRequests.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={
                        errorRate > 1
                          ? "text-red-600"
                          : errorRate > 0.1
                            ? "text-amber-600"
                            : "text-green-600"
                      }
                    >
                      {tenant.totalErrors} ({errorRate.toFixed(2)}%)
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {tenant.avgLatency}ms
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    <LatencyBadge value={tenant.p95Latency} threshold={1000} />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <LatencyBadge value={tenant.p99Latency} threshold={3000} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Shared components
   ──────────────────────────────────────────────────────────── */

function LatencyBadge({
  value,
  threshold,
}: {
  value: number;
  threshold: number;
}) {
  const isHigh = value > threshold;
  return (
    <span
      className={`inline-flex items-center gap-1 ${
        isHigh ? "text-red-600" : "text-green-600"
      }`}
    >
      {isHigh ? (
        <ArrowUp className="h-3 w-3" />
      ) : (
        <ArrowDown className="h-3 w-3" />
      )}
      {value}ms
    </span>
  );
}
